/**
 * FilesUploadProgress — Phase 2.5 (Files UX Refactor 2026-08-08).
 * Phase 2.7 (2026-08-08): + иконка ✕ в строке uploading (AbortController),
 *   + фаза 'cancelled' (client-cancel HTTP; backend dataset мог уже
 *     появиться — см. comment в `FilesUploadPage`).
 *
 * Компонент показывает реальный upload progress (XHR onUploadProgress,
 * НЕ выдуманный %) + статус парсинга после успешного upload.
 *
 * Backend имеет всего 4 статуса (uploaded/parsing/ready/failed) — никаких
 * внутренних стадий. Этот компонент честно отображает:
 *  - uploading: прогресс XHR (bytes loaded / total) + иконка ✕
 *  - uploaded/parsing: Loader2 + elapsed mm:ss
 *  - ready: CheckCircle2 + filename + ссылка на Transform
 *  - failed: red card + error_message + Retry button
 *  - cancelled: neutral card + filename + подсказка + onRetry
 *
 * Timeout UI живёт в FilesResultsPage (см. ниже), а НЕ здесь — потому что
 *  - upload-сценарий: progress идёт ровно пока XHR reads bytes; после
 *    onUploadProgress=100% сервер уже мог создать Dataset и прислать
 *    dataset_id. Тогда control переходит к polling'у в FilesUploadPage
 *    (useDataset), и timeout-визуализация — это уже polling timeout, не
 *    upload timeout.
 *  - F5/reload: upload-фаза утрачена, dataset уже в БД со статусом
 *    uploaded/parsing. Пользователь видит его в FilesResultsPage → там
 *    и должен быть timeout UI.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, RotateCw, XCircle, AlertTriangle, X } from 'lucide-react';

import { cn } from '@/lib/utils';

export type UploadPhase =
  | 'uploading'
  | 'uploaded'
  | 'parsing'
  | 'ready'
  | 'failed'
  | 'cancelled'
  | 'timeout';

export interface FilesUploadProgressProps {
  fileName: string;
  totalBytes: number;
  loadedBytes: number;
  phase: UploadPhase;
  /** ms timestamp момента, когда начали считать elapsed (upload start или upload success).
   *  null когда ещё ничего не началось. */
  startedAt: number | null;
  /** Phase 2.7: когда началась фаза 'parsing'. Если задано — elapsed для
   *  parsing-фазы считается отсюда, а не от startedAt. null = parsing ещё
   *  не стартовал (фаза uploading/uploaded/cancelled/failed/timeout/ready). */
  parsingStartedAt?: number | null;
  errorMessage?: string | null;
  onRetry?: () => void;
  /** Phase 2.7: нажата кнопка ✕ во время uploading. Parent должен вызвать
   *  AbortController.abort() и перевести pendingFile в phase='cancelled'. */
  onCancel?: () => void;
  /** Phase 2.7: нажата кнопка «Обновить статус» во время timeout. Parent
   *  должен сбросить parsingStartedAt (parent управляет polling'ом через
   *  useDataset; refresh = новый parsingStartedAt = ещё один таймаут-цикл). */
  onRefreshStatus?: () => void;
  /** Phase 2.7: «Повторить» во время timeout — отличается от onRetry в failed:
   *  для timeout это = reparse. Parent решает семантику; по умолчанию
   *  FilesUploadPage.handleRetry делает общий reset + abort. */
  onTimeoutRetry?: () => void;
  onOpenTransform?: (datasetId: string) => void;
  datasetId?: string | null;
}

function formatMB(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatElapsed(startedAt: number): string {
  const secs = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

export function FilesUploadProgress({
  fileName,
  totalBytes,
  loadedBytes,
  phase,
  startedAt,
  parsingStartedAt,
  errorMessage,
  onRetry,
  onCancel,
  onRefreshStatus,
  onTimeoutRetry,
  onOpenTransform,
  datasetId,
}: FilesUploadProgressProps) {
  const { t } = useTranslation();
  const isBusy = phase === 'uploading' || phase === 'uploaded' || phase === 'parsing';

  // Tick для обновления elapsed каждую секунду (только пока busy).
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isBusy || startedAt === null) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [isBusy, startedAt]);

  if (phase === 'uploading') {
    const pct = totalBytes > 0 ? Math.min(100, Math.round((loadedBytes / totalBytes) * 100)) : 0;
    const ariaValueNow = Math.min(loadedBytes, totalBytes);
    return (
      <div
        className="card border-primary-500/20 bg-primary-500/5"
        aria-busy="true"
        data-testid="files-upload-progress"
      >
        <div className="flex items-center gap-3 text-sm text-surface-300 mb-2">
          <Loader2 className="w-4 h-4 animate-spin text-primary-400" />
          <span className="font-medium truncate">{fileName}</span>
          <span className="text-xs text-surface-500">
            {t('files.upload.progress', {
              loaded: formatMB(loadedBytes),
              total: formatMB(totalBytes),
              defaultValue: 'Завантажено {{loaded}} / {{total}}',
            })}
          </span>
          {/* Phase 2.7: иконка-крестик для отмены загрузки. По решению Janni —
              inline, рядом с progress. Parent (FilesUploadPage) переводит
              pendingFile.phase в 'cancelled' после abort(). Повторный клик
              идемпотентен: onCancel во время uploading. Дальше компонент
              уходит в ветку 'cancelled' (без ✕ в ней — операция уже завершена). */}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="ml-auto shrink-0 inline-flex items-center justify-center w-7 h-7 rounded text-surface-300 hover:text-danger hover:bg-danger-tint/10 transition-colors"
              aria-label={t('files.upload.cancel', {
                defaultValue: 'Скасувати завантаження',
              })}
              title={t('files.upload.cancel', {
                defaultValue: 'Скасувати завантаження',
              })}
              data-testid="files-upload-cancel"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={totalBytes}
          aria-valuenow={ariaValueNow}
          aria-label={t('files.upload.progress', {
            loaded: formatMB(loadedBytes),
            total: formatMB(totalBytes),
            defaultValue: 'Завантажено {{loaded}} / {{total}}',
          })}
          className="h-1.5 w-full overflow-hidden rounded bg-surface-700/50"
        >
          <div
            className="h-full bg-primary-400 transition-[width] duration-150"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  }

  if (phase === 'uploaded' || phase === 'parsing') {
    // Phase 2.7: для parsing используем parsingStartedAt (если задан),
    // иначе startedAt (фаза uploaded — ещё не стартовал parsing).
    const elapsedStartedAt = parsingStartedAt ?? startedAt;
    const elapsed = elapsedStartedAt !== null ? formatElapsed(elapsedStartedAt) : '00:00';
    return (
      <div
        className="card flex items-center gap-3 text-sm text-surface-300 border-primary-500/20 bg-primary-500/5"
        aria-busy="true"
        data-testid="files-upload-progress"
      >
        <Loader2 className="w-4 h-4 animate-spin text-primary-400" />
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{fileName}</p>
          <p className="text-xs text-surface-500">
            {t('files.upload.parsing_stepper.processing', {
              defaultValue: 'Обробляємо файл…',
            })}
            {' · '}
            {t('files.upload.parsing_stepper.elapsed', {
              mm: elapsed.split(':')[0],
              ss: elapsed.split(':')[1],
              defaultValue: '{{mm}}:{{ss}}',
            })}
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'ready') {
    return (
      <div
        className="card flex items-center gap-3 text-sm text-success border-success-tint/30 bg-success-tint/5"
        aria-busy="false"
        data-testid="files-upload-progress"
      >
        <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">
            {fileName}{' · '}
            {t('files.status.ready', { defaultValue: 'Готово' })}
          </p>
          {datasetId && onOpenTransform && (
            <button
              type="button"
              onClick={() => onOpenTransform(datasetId)}
              className="text-xs text-success hover:text-success underline mt-0.5"
              data-testid="files-upload-open-transform"
            >
              {t('files.upload.successHint', {
                defaultValue: 'Відкрити в Трансформації',
              })}
            </button>
          )}
        </div>
      </div>
    );
  }

  // phase === 'failed'
  if (phase === 'failed') {
    return (
      <div
        className={cn(
          'card border-danger-tint/30 bg-danger-tint/5',
        )}
        aria-busy="false"
        data-testid="files-upload-progress"
      >
        <div className="flex items-start gap-3 text-sm text-danger">
          <XCircle className="w-4 h-4 text-danger flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">
              {fileName}
              {' · '}
              {t('files.upload.parsing_stepper.failed', {
                defaultValue: 'Не вдалося обробити файл',
              })}
            </p>
            {errorMessage && (
              <p
                className="mt-1 text-xs text-danger whitespace-normal break-words"
                data-testid="files-upload-error-message"
              >
                <AlertTriangle className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                {errorMessage}
              </p>
            )}
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="btn-ghost text-xs mt-2 border border-danger-tint/30 hover:bg-danger-tint/10"
                aria-label={t('files.list.retry', { defaultValue: 'Повторити' })}
                data-testid="files-upload-retry"
              >
                <RotateCw className="w-3.5 h-3.5 mr-1.5" />
                {t('files.list.retry', { defaultValue: 'Повторити' })}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // phase === 'cancelled' — клиент оборвал XHR. Сервер мог уже принять файл
  // и создать Dataset — это видно в DatasetList ниже. Текст НЕ обещает
  // пользователю, что отмена гарантированно отменила обработку на сервере
  // (axios cancel = только client-side). Retry просто сбрасывает pendingFile
  // и оставляет пользователя выбрать файл заново.
  if (phase === 'cancelled') {
    return (
      <div
        className="card border-surface-700 bg-panel"
        aria-busy="false"
        data-testid="files-upload-progress"
      >
        <div className="flex items-start gap-3 text-sm text-surface-300">
          <XCircle className="w-4 h-4 text-surface-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">
              {fileName}
              {' · '}
              {t('files.upload.cancelled.title', {
                defaultValue: 'Завантаження скасовано',
              })}
            </p>
            <p className="mt-1 text-xs text-surface-500 whitespace-normal break-words">
              {t('files.upload.cancelled.hint', {
                defaultValue:
                  'Якщо файл уже був прийнятий сервером, його статус можна перевірити в списку нижче.',
              })}
            </p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="btn-ghost text-xs mt-2 border border-surface-700 hover:bg-surface-800"
                aria-label={t('files.list.retry', { defaultValue: 'Повторити' })}
                data-testid="files-upload-retry"
              >
                <RotateCw className="w-3.5 h-3.5 mr-1.5" />
                {t('files.list.retry', { defaultValue: 'Повторити' })}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // phase === 'timeout' — parsing polling превысил PARSING_TIMEOUT_MS.
  // Это НЕ 'failed' (бэк сам не перевёл dataset в failed). Это НЕ 'worker
  // down' (бэк не сообщает о worker'е; не утверждаем того, чего не знаем).
  // Текст — нейтральный: «обработка занимает дольше обычного». Действия:
  //  - Retry (reparse через /reparse) — родитель решает: FilesUploadPage
  //    сейчас шлёт reset (как failed), но в дальнейшем можно прицепить
  //    reparse; чтобы не плодить варианты, делаем общий reset как для failed.
  //  - Обновить статус — parent делает setPendingFile с новым
  //    parsingStartedAt; useDataset продолжает polling.
  //  - Закрыть — отдельной кнопки нет: phase='timeout' → закрыть = клик
  //    на родительский крестик в списке (или refresh страницы).
  if (phase === 'timeout') {
    return (
      <div
        className="card border-warning-tint/30 bg-warning-tint/5"
        aria-busy="false"
        data-testid="files-upload-progress"
        data-phase="timeout"
      >
        <div className="flex items-start gap-3 text-sm text-warning">
          <Loader2 className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">
              {fileName}
              {' · '}
              {t('files.upload.timeout.title', {
                defaultValue: 'Обробка триває довше звичайного',
              })}
            </p>
            <p className="mt-1 text-xs text-warning/80 whitespace-normal break-words">
              {t('files.upload.timeout.hint', {
                defaultValue:
                  'Можливо, задача очікує обробки. Можна повторити, оновити статус або повернутися.',
              })}
            </p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {(onTimeoutRetry || onRetry) && (
                <button
                  type="button"
                  onClick={() => (onTimeoutRetry ?? onRetry)?.()}
                  className="btn-ghost text-xs border border-warning-tint/30 hover:bg-warning-tint/10"
                  aria-label={t('files.list.retry', { defaultValue: 'Повторити' })}
                  data-testid="files-upload-timeout-retry"
                >
                  <RotateCw className="w-3.5 h-3.5 mr-1.5" />
                  {t('files.list.retry', { defaultValue: 'Повторити' })}
                </button>
              )}
              {onRefreshStatus && (
                <button
                  type="button"
                  onClick={onRefreshStatus}
                  className="btn-ghost text-xs border border-surface-700 hover:bg-surface-800"
                  aria-label={t('files.upload.timeout.refresh', {
                    defaultValue: 'Оновити статус',
                  })}
                  data-testid="files-upload-timeout-refresh"
                >
                  {t('files.upload.timeout.refresh', {
                    defaultValue: 'Оновити статус',
                  })}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // phase === 'ready' (последний, чтобы не падать в default ветку)
  if (phase === 'ready') {
    return (
      <div
        className="card flex items-center gap-3 text-sm text-success border-success-tint/30 bg-success-tint/5"
        aria-busy="false"
        data-testid="files-upload-progress"
      >
        <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">
            {fileName}{' · '}
            {t('files.status.ready', { defaultValue: 'Готово' })}
          </p>
          {datasetId && onOpenTransform && (
            <button
              type="button"
              onClick={() => onOpenTransform(datasetId)}
              className="text-xs text-success hover:text-success underline mt-0.5"
              data-testid="files-upload-open-transform"
            >
              {t('files.upload.successHint', {
                defaultValue: 'Відкрити в Трансформації',
              })}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Недостижимо: все ветки покрыты выше.
  return null;
}
